/*
  navConfig
  ------
  This powers the top bar navigation on desktop and mobile
  Adapted from bee-hive navigation config for ts-perks
*/
import type { WhoAmIPermissions } from '../../../../types/index'

export type NavItem = {
  title: string
  link: string
  isVisibleForUserRole: boolean
  disabled: boolean
}

export type NavDropdown = {
  title: string
  isVisibleForUserRole: boolean
  subItems: NavItem[]
}

export const isNavDropdown = (
  item: NavItem | NavDropdown,
): item is NavDropdown => {
  return Object.hasOwnProperty.call(item, 'subItems')
}

export const useNavConfig = (
  userPermissions: WhoAmIPermissions | null,
): Array<NavItem | NavDropdown> => {
  const PRODUCTION = 'production'
  const env = import.meta.env.VITE_ENVIRONMENT // 'development' | 'production'

  const legacyUniverseBaseUrl =
    env === PRODUCTION
      ? 'https://universe.techstars.com'
      : 'https://universe.techstarsdev.com'

  return [
    {
      title: 'fundraising',
      isVisibleForUserRole: Boolean(
        userPermissions?.canWriteMDProfile ||
          userPermissions?.canWriteProgramStaffProfile ||
          userPermissions?.isCeo,
      ),
      subItems: [
        {
          title: 'fundraising home',
          link: `${legacyUniverseBaseUrl}/fundraising`,
          isVisibleForUserRole: userPermissions?.isCeo ?? false,
          disabled: false,
        },
        {
          title: 'elevator pitch gpt tool',
          link: `${legacyUniverseBaseUrl}/elevator-pitch`,
          isVisibleForUserRole: userPermissions?.isCeo ?? false,
          disabled: false,
        },
        {
          title: 'saved investors',
          link: `${legacyUniverseBaseUrl}/investor-lists`,
          isVisibleForUserRole: userPermissions?.canReadInvestorList ?? false,
          disabled: false,
        },
        {
          title: 'valuation data tool',
          link: `${legacyUniverseBaseUrl}/valuation-data-tool`,
          isVisibleForUserRole: true, // open to all users with access to this dropdown (ceo, md, program staff)
          disabled: false,
        },
        userPermissions?.isCeo
          ? {
              title: 'Metal for Techstars ↗️',
              link: 'https://www.metal.so/metal-for-techstars',
              isVisibleForUserRole: true,
              disabled: false,
            }
          : {
              title: 'program fundraising home',
              link: '/programs/pipelines',
              isVisibleForUserRole:
                (userPermissions?.canWriteMDProfile ||
                  userPermissions?.canWriteProgramStaffProfile) ??
                false,
              disabled: false,
            },
      ],
    },
    {
      title: 'network',
      isVisibleForUserRole: true,
      subItems: [
        {
          title: 'entrepreneur directory',
          link: 'https://community.techstars.com/techstars-founders-3jvjf6r1',
          isVisibleForUserRole: true,
          disabled: false,
        },
        {
          title: 'company directory',
          link: 'https://community.techstars.com/companies-oqd2369k',
          isVisibleForUserRole: true,
          disabled: false,
        },
        {
          title: 'investor directory',
          link: `${legacyUniverseBaseUrl}/directory/investors`,
          isVisibleForUserRole: true,
          disabled: false,
        },
        {
          title: 'investment firm directory',
          link: `${legacyUniverseBaseUrl}/directory/investment-firms`,
          isVisibleForUserRole: true,
          disabled: false,
        },
      ],
    },
    {
      title: 'resources',
      link: 'https://community.techstars.com/accelerator-alumni-uomzvup7',
      isVisibleForUserRole: true,
      disabled: false,
    },
    {
      title: 'perks',
      link: '/',
      isVisibleForUserRole: true,
      disabled: false,
    },
    {
      title: 'community',
      link:
        env === PRODUCTION
          ? 'https://community.techstars.com'
          : 'https://community-sandbox.techstarsdev.com',
      isVisibleForUserRole: true,
      disabled: false,
    },
  ]
}
